import { JUMP } from '../inputBits'
import type { InputBits } from '../inputBits'
import { feetInAny } from '../terrain/zones'
import { NO_TICK } from '../world'
import type { World } from '../world'
import { activeRide, bonusPct } from './rides'

/**
 * Step 2: horizontal speed and gravity. Returns this tick's vertical displacement: the average
 * of the old and new `vy` (exact for constant acceleration, so jump apex matches v²/2g).
 * Everyone runs at one speed (D-045); boosts add to it, mud and slow strips cut it. A tumbling (stunned)
 * runner drifts on at `tumbleSpeedPct` of base speed, a skidding one (Oil Slick) at `skidSpeedPct`. A rider adds its ride's `speedPct` (with any
 * boost, capped at `maxSpeedPct`); a Jet Snail ignores mud and slow strips; a Pufferloon falls at
 * `fallSpeedPct` of the cap and rises at `glideLiftPxPerTick` while JUMP is held in the air.
 * Respawning players do not move; finished ones stop `finishRunoutPx` past the finish line.
 */
export function integrateVelocity(world: World, i: number, bits: InputBits): number {
  const p = world.players[i]!
  if (p.respawnAtTick !== NO_TICK) return 0
  const { cfg, tick } = world

  let vx: number
  if (p.stunnedUntil > tick) {
    vx = (cfg.baseSpeedPxPerTick * cfg.tumbleSpeedPct) / 100
  } else if (p.skidUntil > tick) {
    vx = (cfg.baseSpeedPxPerTick * cfg.items.oilSlick.skidSpeedPct) / 100
  } else {
    const boosted = p.boostUntil > tick
    vx = (cfg.baseSpeedPxPerTick * (100 + bonusPct(world, p, boosted ? p.boostPct : 0, tick))) / 100
    if (!(boosted && p.boostIgnoresMud) && !activeRide(world, p)?.ignoresSlow) {
      if (feetInAny(world.track, p, 'mud')) vx = (vx * cfg.mudSpeedPct) / 100
      if (feetInAny(world.track, p, 'slowStrip')) vx = (vx * cfg.slowStripPct) / 100
    }
  }
  if (p.placement !== 0) {
    // Finish run-out: jog on, then stop exactly `finishRunoutPx` past the line (D-036).
    vx = Math.max(0, Math.min(vx, world.track.finishX + cfg.finishRunoutPx - p.x))
  }
  p.vx = vx

  const vy0 = p.vy
  const ride = activeRide(world, p)
  const glide = ride?.ability === 'glide' ? ride : null
  const maxFall = (cfg.maxFallSpeedPxPerTick * (glide ? glide.fallSpeedPct : 100)) / 100
  p.vy = Math.min(vy0 + cfg.gravityPxPerTick2, maxFall)
  if (glide && !p.grounded && (bits & JUMP) !== 0) p.vy = Math.min(p.vy, -glide.glideLiftPxPerTick)
  return (vy0 + p.vy) / 2
}
